// Extract closing balances from a prior period's financials (xlsx or PDF) using the extraction engine (tool-use).
// Returned shape (per item):
//   { accountCode, accountName, type, balance, sourceLabel, confidence, notes }
// The caller converts `balance` (signed, on the natural side) into {dr, cr} for Firestore storage.

import { authHeader } from './firebase';

const MODEL = 'claude-sonnet-5';
// Routed through our own backend so the provider key never reaches the browser.
const ENGINE_URL = (process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001') + '/api/engine';

const TOOL = {
  name: 'submit_opening_balances',
  description: 'Submit the closing balances extracted from a prior accounting period to be carried forward as opening balances.',
  input_schema: {
    type: 'object',
    properties: {
      periodEnd: {
        type: 'string',
        description: 'ISO date (YYYY-MM-DD) the prior financials are reported as at. Leave empty string if not visible.',
      },
      currency: { type: 'string', description: 'Three-letter currency code, e.g. ZAR.' },
      balances: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            accountCode: { type: 'string', description: 'GL code from the supplied chart of accounts.' },
            accountName: { type: 'string', description: 'Account name from the chart of accounts.' },
            type: {
              type: 'string',
              enum: ['asset', 'liability', 'equity', 'revenue', 'cogs', 'expense', 'other_income', 'other_expense'],
            },
            balance: {
              type: 'number',
              description: 'Signed balance on the account natural side (asset/expense = dr-natural; liability/equity/revenue = cr-natural). Positive = normal direction. Negative = contra.',
            },
            sourceLabel: {
              type: 'string',
              description: 'Verbatim line label as it appeared in the source document (e.g. "Bank - FNB Transactional", "VAT Control").',
            },
            confidence: { type: 'string', enum: ['high', 'medium', 'low'] },
            notes: { type: 'string', description: 'Optional explanation, especially for low-confidence mappings.' },
          },
          required: ['accountCode', 'accountName', 'type', 'balance', 'sourceLabel'],
        },
      },
    },
    required: ['balances'],
  },
};

function buildSystem(project, coa) {
  const coaLines = coa.map((a) => '  ' + a.code + '  ' + a.name + '  (' + a.type + ')').join('\n');
  return (
    'You are Tlhakiso, a South African accounting assistant. You are extracting CLOSING BALANCES from a prior period\'s financial statements so they can be posted as OPENING BALANCES for a new accounting period.\n\n' +
    'COMPANY: ' + project.name + (project.type ? ' (' + project.type + ')' : '') + '\n' +
    'NEW PERIOD STARTS: ' + (project.yearStart || '?') + '\n\n' +
    'CHART OF ACCOUNTS (use ONLY these codes):\n' + coaLines + '\n\n' +
    'RULES:\n' +
    '1. Only Asset, Liability and Equity accounts carry forward. Revenue, COGS and Expense accounts reset to zero at the start of a new fiscal year — do NOT include them in the output unless the source document is explicitly a mid-year handover snapshot.\n' +
    '2. If you see a "Current Year Earnings", "Net Profit for the year" or "Profit/Loss for the period" amount on a prior-year balance sheet, ROLL IT INTO Retained Earnings (300020) and do not output a separate Current Year Earnings line. The new period starts with CYE at zero.\n' +
    '3. Use the signed convention: balance is positive when the account is on its natural side (Assets dr-positive, Liabilities/Equity cr-positive). A bank overdraft would be a *liability* line, not a negative-asset.\n' +
    '4. Match each source line to the single most appropriate account code from the chart above. If the source has multiple bank lines and the chart has multiple bank accounts, match by name where possible; otherwise put everything to 100010 Bank - Transactional and flag confidence=low.\n' +
    '5. If you can\'t map a line confidently, still output it with the best-guess code and confidence=low + notes explaining the ambiguity.\n' +
    '6. Amounts: parse rand values, strip currency symbols, treat (123) and -123 as negative. Use 2 decimals.\n' +
    '7. Skip subtotals, totals, and section headings — output only individual account-level balances.\n' +
    '8. Call the submit_opening_balances tool exactly once with all balances.'
  );
}

// xlsx ArrayBuffer → text. Converts every sheet to CSV and concatenates with sheet-name separators.
export function xlsxToText(arrayBuffer, XLSX) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  return wb.SheetNames.map((name) => {
    const csv = XLSX.utils.sheet_to_csv(wb.Sheets[name], { FS: ',' });
    return '=== Sheet: ' + name + ' ===\n' + csv;
  }).join('\n\n');
}

export async function extractOpeningBalancesFromText({ project, coa, text, sourceLabel }) {
  if (!text || !text.trim()) throw new Error('No text content to extract from');

  const res = await fetch(ENGINE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 8192,
      system: buildSystem(project, coa),
      tools: [TOOL],
      tool_choice: { type: 'tool', name: 'submit_opening_balances' },
      messages: [
        {
          role: 'user',
          content:
            'Extract the closing balances from this prior-period financial statement (' + (sourceLabel || 'uploaded file') + '). ' +
            'Return them via the submit_opening_balances tool.\n\n' + text,
        },
      ],
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error('API ' + res.status + ': ' + err.slice(0, 400));
  }
  return parseToolResponse(await res.json());
}

export async function extractOpeningBalancesFromPdf({ project, coa, pdfBase64, sourceLabel }) {
  if (!pdfBase64) throw new Error('No PDF data provided');

  const res = await fetch(ENGINE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 8192,
      system: buildSystem(project, coa),
      tools: [TOOL],
      tool_choice: { type: 'tool', name: 'submit_opening_balances' },
      messages: [
        {
          role: 'user',
          content: [
            { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: pdfBase64 } },
            {
              type: 'text',
              text:
                'Extract closing balances from this prior-period financial statement PDF (' + (sourceLabel || 'uploaded PDF') + '). ' +
                'Return them via the submit_opening_balances tool.',
            },
          ],
        },
      ],
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error('API ' + res.status + ': ' + err.slice(0, 400));
  }
  return parseToolResponse(await res.json());
}

function parseToolResponse(data) {
  const toolUse = (data.content || []).find((b) => b.type === 'tool_use' && b.name === 'submit_opening_balances');
  if (!toolUse) {
    throw new Error('The extraction engine returned an unexpected response. Please try again. ' + JSON.stringify(data).slice(0, 200));
  }
  const input = toolUse.input || {};
  return {
    periodEnd: input.periodEnd || '',
    currency: input.currency || 'ZAR',
    balances: input.balances || [],
    rawResponse: data,
  };
}

// Convert a list of {accountCode, type, balance} into the Firestore {dr, cr} shape used by computeFromJournals.
// dr-natural types (asset, expense, cogs, other_expense): positive balance → dr; negative → cr.
// cr-natural types (liability, equity, revenue, other_income): positive balance → cr; negative → dr.
const DR_NATURAL = new Set(['asset', 'expense', 'cogs', 'other_expense']);

export function toFirestoreBalances(items) {
  const out = {};
  (items || []).forEach((it) => {
    const amount = Number(it.balance) || 0;
    if (amount === 0) return;
    const drNatural = DR_NATURAL.has(it.type);
    const positive = amount > 0;
    const abs = Math.abs(amount);
    const dr = (drNatural ? positive : !positive) ? abs : 0;
    const cr = (drNatural ? positive : !positive) ? 0 : abs;
    if (!out[it.accountCode]) out[it.accountCode] = { dr: 0, cr: 0 };
    out[it.accountCode].dr += dr;
    out[it.accountCode].cr += cr;
  });
  return out;
}

// Validate proposed balances against the chart of accounts. Returns array of warning strings.
export function validateBalances(items, coa) {
  const codes = new Set(coa.map((a) => a.code));
  const byCode = {};
  coa.forEach((a) => { byCode[a.code] = a; });
  const warnings = [];
  (items || []).forEach((it, i) => {
    if (!codes.has(it.accountCode)) {
      warnings.push('Row ' + (i + 1) + ' (' + it.sourceLabel + '): unknown account code "' + it.accountCode + '"');
    } else if (byCode[it.accountCode].type !== it.type) {
      warnings.push('Row ' + (i + 1) + ' (' + it.sourceLabel + '): type mismatch — chart says ' + byCode[it.accountCode].type + ', extracted as ' + it.type);
    }
    if (typeof it.balance !== 'number' || isNaN(it.balance)) {
      warnings.push('Row ' + (i + 1) + ' (' + it.sourceLabel + '): non-numeric balance');
    }
  });
  // Trial-balance style check: dr-natural totals minus cr-natural totals should be ~0 if A=L+E.
  let drNet = 0, crNet = 0;
  (items || []).forEach((it) => {
    const amt = Number(it.balance) || 0;
    if (DR_NATURAL.has(it.type)) drNet += amt; else crNet += amt;
  });
  const diff = drNet - crNet;
  if (Math.abs(diff) > 0.01) {
    warnings.push('Balance check: Σ(dr-natural balances) − Σ(cr-natural balances) = R ' + diff.toFixed(2) + '. Should be 0 if the prior balance sheet balances. Review for missing or duplicate lines.');
  }
  return warnings;
}
